import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/utils/dashboard/apiError';
import type { RateCard } from '@/types/dashboard/rate';
import { RateFormModal } from './RateFormModal';
import { useDeleteRate, useRateCards } from './useRates';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });

const pct = (fraction: string) => `${Math.round(Number(fraction) * 10000) / 100}%`;

export function RatesPage() {
  const { data: rates = [], isLoading, isError, error, refetch } = useRateCards();
  const deleteRate = useDeleteRate();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<RateCard | null>(null);
  const [toDelete, setToDelete] = useState<RateCard | null>(null);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await deleteRate.mutateAsync(toDelete.id);
      toast.success('Rate card deleted');
      setToDelete(null);
    } catch (err) {
      // Kiosk.rate_charge is PROTECT, so a card still used by kiosks cannot be deleted.
      toast.error(getApiErrorMessage(err, 'Could not delete. It may still be assigned to kiosks.'));
    }
  };

  return (
    <div className="d-flex flex-column gap-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
        <div>
          <h2 className="text-headline-lg text-on-surface mb-1">Rates</h2>
          <p className="text-body-md text-on-surface-variant mb-0">Per-vendor print pricing and fees.</p>
        </div>
        <button
          type="button"
          className="btn btn-primary tap-target"
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
        >
          <i className="bi bi-plus-lg me-2" />
          Onboard rate card
        </button>
      </div>

      <div className="card shadow-kiosk-sm">
        <div className="card-body">
          {isLoading && (
            <div className="text-center py-5 text-on-surface-variant">
              <span className="spinner-border spinner-border-sm me-2" />
              Loading rates…
            </div>
          )}

          {isError && (
            <div className="alert alert-danger d-flex align-items-center justify-content-between" role="alert">
              <span className="text-body-sm">{getApiErrorMessage(error, 'Could not load rates')}</span>
              <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => refetch()}>
                Retry
              </button>
            </div>
          )}

          {!isLoading && !isError && rates.length === 0 && (
            <div className="text-center py-5">
              <div className="text-headline-sm text-on-surface">No rate cards yet</div>
              <p className="text-body-md text-on-surface-variant">Onboard a rate card to price a vendor&apos;s kiosks.</p>
            </div>
          )}

          {rates.length > 0 && (
            <div className="table-responsive">
              <table className="table align-middle mb-0">
                <thead>
                  <tr className="text-label-sm text-on-surface-variant">
                    <th className="bg-surface-low border-0 rounded-start-3">Vendor</th>
                    <th className="bg-surface-low border-0">B/W</th>
                    <th className="bg-surface-low border-0">Colour</th>
                    <th className="bg-surface-low border-0">Duplex off</th>
                    <th className="bg-surface-low border-0">Merchant fee</th>
                    <th className="bg-surface-low border-0">Convenience fee</th>
                    <th className="bg-surface-low border-0">Effective from</th>
                    <th className="bg-surface-low border-0">Kiosks</th>
                    <th className="bg-surface-low border-0">Status</th>
                    <th className="bg-surface-low border-0 rounded-end-3 text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rates.map((r) => (
                    <tr key={r.id} className="text-body-sm text-on-surface">
                      <td className="fw-semibold">{r.vendor_name}</td>
                      <td>₹{r.bw_rate_per_page}</td>
                      <td>₹{r.color_rate_per_page}</td>
                      <td>{pct(r.duplex_extra_charge)}</td>
                      <td>₹{r.merchant_fees}</td>
                      <td>₹{r.convenience_fee}</td>
                      <td className="text-on-surface-variant">{formatDate(r.effective_from)}</td>
                      <td>{r.kiosk_count ?? 0}</td>
                      <td>
                        <span
                          className={`badge rounded-pill ${
                            r.is_active ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'
                          }`}
                        >
                          {r.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="text-end text-nowrap">
                        <button
                          type="button"
                          className="btn btn-link text-on-surface-variant p-1"
                          title="Edit"
                          onClick={() => {
                            setEditing(r);
                            setFormOpen(true);
                          }}
                        >
                          <i className="bi bi-pencil fs-6" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-link text-danger p-1"
                          title="Delete"
                          onClick={() => setToDelete(r)}
                        >
                          <i className="bi bi-trash fs-6" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {formOpen && <RateFormModal rate={editing} onClose={() => setFormOpen(false)} />}

      <Modal show={toDelete !== null} onHide={() => setToDelete(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="text-headline-sm">Delete rate card</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-body-md">
          Delete the rate card for <strong>{toDelete?.vendor_name}</strong>? Kiosks using it must be reassigned first.
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-outline-secondary" onClick={() => setToDelete(null)}>
            Cancel
          </button>
          <button type="button" className="btn btn-danger" onClick={confirmDelete} disabled={deleteRate.isPending}>
            {deleteRate.isPending ? 'Deleting…' : 'Delete'}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
